//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  ContentLibrarySortType,
  MediaFilterParamKey,
  MediaSourceParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import {
  EmptyFilters,
  EmptySources,
} from '@media-library/constants/initialMediaLibraryItemParams';

import { makeStateFactoryQueryParams } from 'app/initialization/routing/stateHelpers';
import { RoutableStateName } from 'app/top-level-states';

export const MediaLibrarySubPageTitles = {
  ALL_MEDIA: 'All Media',
  TEAM_MEDIA: 'Team Uploads',
  MY_MEDIA: 'My Uploads',
} as const;

export const MediaLibraryStateNames: RoutableStateName[] = [
  'library.all-media',
  'library.team-media',
  'library.my-media',
];

export type MediaLibrarySubPages = keyof typeof MediaLibrarySubPageTitles;

// Serialized type for media library url query as `useCurrentStateAndParams` does not include input validation and type conversion functionalities. Further validation and deserialization are required.
export type MediaLibraryQueryParams = Partial<
  Record<
    | MediaFilterParamKey
    | MediaSourceParamKey
    | typeof MediaLibrarySortTypeQueryKey,
    string
  >
>;

const mediaFilterKeys: string[] = Object.keys(EmptyFilters);

const mediaSourceKeys: string[] = Object.keys(EmptySources);

export const MediaLibrarySortTypeQueryKey = 'orderBy';

// URL query key searched by universal-image-actions-service and global-uia-laucher to determine whether UIA should be launched and the item to open.
const UIAOpenItemQueryKey = 'openItem';

export const mediaLibraryQueryParams = [
  ...mediaFilterKeys,
  ...mediaSourceKeys,
  MediaLibrarySortTypeQueryKey,
  UIAOpenItemQueryKey,
];

export const mediaLibraryQueryKeys: string = makeStateFactoryQueryParams(
  mediaLibraryQueryParams,
);

export enum ContentLayout {
  Grid = 'grid',
  List = 'list',
}

export enum TopBarLayout {
  Default = 'default',
  WideSearch = 'wide-search',
}

export type MediaLibrarySortType =
  | ContentLibrarySortType.AI_SIMILARITY_SEARCH
  | ContentLibrarySortType.TIME
  | ContentLibrarySortType.LIKES
  | ContentLibrarySortType.COMMENTS
  | ContentLibrarySortType.ENGAGEMENT
  | ContentLibrarySortType.FOLLOWERS
  | ContentLibrarySortType.VISUAL_SCORE
  | ContentLibrarySortType.MODERATED_TIMESTAMP
  | ContentLibrarySortType.SYNDICATION_TIMESTAMP
  | ContentLibrarySortType.SIMILARITY_TO_APPROVED
  | ContentLibrarySortType.PRODUCTS_TAGGED
  | ContentLibrarySortType.RELEVANCY_SCORE;

export const MediaLibrarySortLabels: Record<MediaLibrarySortType, string> = {
  [ContentLibrarySortType.AI_SIMILARITY_SEARCH]: 'AI Ranked',
  [ContentLibrarySortType.TIME]: 'Most Recently Posted',
  [ContentLibrarySortType.LIKES]: 'Most Likes',
  [ContentLibrarySortType.COMMENTS]: 'Most Comments',
  [ContentLibrarySortType.ENGAGEMENT]: 'Most Engaging',
  [ContentLibrarySortType.FOLLOWERS]: 'Most Followers',
  [ContentLibrarySortType.VISUAL_SCORE]: 'Most On-Brand',
  [ContentLibrarySortType.MODERATED_TIMESTAMP]: 'Most Recently Moderated',
  [ContentLibrarySortType.SYNDICATION_TIMESTAMP]: 'Recently Moderated',
  [ContentLibrarySortType.SIMILARITY_TO_APPROVED]: 'Similarity to Approved',
  [ContentLibrarySortType.PRODUCTS_TAGGED]: 'Most Products Featured',
  [ContentLibrarySortType.RELEVANCY_SCORE]: 'Recommended',
};

export const MediaLibrarySortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.TIME,
  ContentLibrarySortType.COMMENTS,
  ContentLibrarySortType.LIKES,
  ContentLibrarySortType.ENGAGEMENT,
  ContentLibrarySortType.VISUAL_SCORE,
] as const;

export const StreamSortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.TIME,
  ContentLibrarySortType.COMMENTS,
  ContentLibrarySortType.LIKES,
  ContentLibrarySortType.ENGAGEMENT,
  ContentLibrarySortType.FOLLOWERS,
  ContentLibrarySortType.VISUAL_SCORE,
  ContentLibrarySortType.SIMILARITY_TO_APPROVED,
] as const;

export const PartnerStreamSortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.TIME,
  ContentLibrarySortType.COMMENTS,
  ContentLibrarySortType.LIKES,
  ContentLibrarySortType.ENGAGEMENT,
  ContentLibrarySortType.FOLLOWERS,
  ContentLibrarySortType.SYNDICATION_TIMESTAMP,
  ContentLibrarySortType.SIMILARITY_TO_APPROVED,
  ContentLibrarySortType.VISUAL_SCORE,
] as const;

export const PartnerStreamSortLabels: Partial<
  Record<MediaLibrarySortType, string>
> = {
  [ContentLibrarySortType.TIME]: 'Most Recent',
};

interface GetBaseSortTypeOptionsParams {
  isPartnerStreamView: boolean;
  isStreamsExperienceEnabled: boolean;
}

interface GetSortTypeOptionsParams extends GetBaseSortTypeOptionsParams {
  isSimilarityToApprovedEnabled: boolean;
}

// TODO: PD-319070 - Return StreamSortTypeOptions for all non-partner views once media_library_streams is released
const getBaseSortTypeOptions = ({
  isPartnerStreamView,
  isStreamsExperienceEnabled,
}: GetBaseSortTypeOptionsParams): MediaLibrarySortType[] => {
  if (isPartnerStreamView) {
    return PartnerStreamSortTypeOptions;
  }

  return isStreamsExperienceEnabled
    ? StreamSortTypeOptions
    : MediaLibrarySortTypeOptions;
};

export const getSortTypeOptions = ({
  isPartnerStreamView,
  isSimilarityToApprovedEnabled,
  isStreamsExperienceEnabled,
}: GetSortTypeOptionsParams): MediaLibrarySortType[] => {
  const options = getBaseSortTypeOptions({
    isPartnerStreamView,
    isStreamsExperienceEnabled,
  });

  return isSimilarityToApprovedEnabled
    ? options
    : options.filter(
        (option) => option !== ContentLibrarySortType.SIMILARITY_TO_APPROVED,
      );
};

export const ModerationReadyToDistributeSortTypeOptions: MediaLibrarySortType[] =
  [
    ContentLibrarySortType.RELEVANCY_SCORE,
    ContentLibrarySortType.TIME,
    ContentLibrarySortType.MODERATED_TIMESTAMP,
    ContentLibrarySortType.PRODUCTS_TAGGED,
    ContentLibrarySortType.ENGAGEMENT,
  ] as const;

export const ModerationNeedsActionSortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.RELEVANCY_SCORE,
  ContentLibrarySortType.TIME,
  ContentLibrarySortType.MODERATED_TIMESTAMP,
  ContentLibrarySortType.ENGAGEMENT,
] as const;

export const ModerationRejectedSortTypeOptions: MediaLibrarySortType[] = [
  ContentLibrarySortType.MODERATED_TIMESTAMP,
  ContentLibrarySortType.TIME,
] as const;

export const MANAGE_COLLECTIONS_PERMISSION = 'library:manage_collections';
export const CREATE_OR_UPDATE_USER_PERMISSION = 'admin:create_or_update_user';
